import { useState } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { formSchema, FormValues } from './signupFormSchema';

export const useSignupForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange", // Enable real-time validation
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      desiredOutcome: "",
      agreeToTerms: false
    }
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      console.log('🚀 Form submission started with data:', data);
      
      // Validate required fields
      if (!data.name || !data.email || !data.company || !data.phone || !data.agreeToTerms) {
        console.log('❌ Missing required fields');
        toast.error("Please fill in all required fields and agree to the terms.");
        return;
      }
      
      console.log('🌐 Current URL:', window.location.href);
      console.log('🏠 Origin:', window.location.origin);
      
      // Create form data for Netlify submission using proper encoding
      const formData = new FormData();
      formData.append('form-name', 'synthreo-signup');
      
      // Add all form fields exactly as they appear in the static form
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('company', data.company);
      formData.append('phone', data.phone);
      formData.append('desiredOutcome', data.desiredOutcome || '');
      formData.append('agreeToTerms', data.agreeToTerms ? 'true' : 'false');
      
      console.log('📦 FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`);
      }
      
      console.log('📡 Sending request to Netlify...');
      
      // Submit to Netlify using FormData (better for Netlify Forms)
      const response = await fetch('/', {
        method: 'POST',
        body: formData
      });
      
      console.log('📨 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
      
      if (!response.ok) {
        const responseText = await response.text();
        console.error('❌ Response error details:', responseText);
        throw new Error(`Network response error: ${response.status} ${response.statusText}`);
      }
      
      console.log('✅ Form submitted successfully to Netlify');
      toast.success("Account request submitted successfully!");
      
      // Reset form
      form.reset();
      
      // Navigate to success page
      console.log('🔄 Navigating to success page...');
      navigate("/auth/signup?signup=success");
      
    } catch (error) {
      console.error("❌ Form submission error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
      console.log('🏁 Form submission process completed');
    }
  };

  return {
    form,
    isSubmitting,
    onSubmit
  };
};
